/**
 * Board 1 · The prepared protocol: what Ready means, where its live tickets stand, the protocol's switch, its
 * promoted and not promoted scenarios, a summary of its writes, and the way to History. Board 6's banner leads it
 * while the promotion is on old wording.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { LivePoolView } from '../types';
import { formatWhen, isPromoted, promotedSummaryOf } from './scenarios';
import { writeSummariesOf, type OverviewRow } from './view-rules';

/** Board 6 · The protocol was compiled again after it was promoted. */
export const OutdatedBanner = ({
	outdated,
	canEdit,
	busy,
	onPromoteAgain
}: {
	outdated: NonNullable<LivePoolView['outdated']>;
	canEdit: boolean;
	busy: boolean;
	onPromoteAgain: () => void;
}) => {
	const lastAlert = outdated.alertsSent.length ? Math.max(...outdated.alertsSent) : null;
	return (
		<div className='flex flex-col gap-2 rounded-[10px] border border-yellow-border bg-yellow-bg px-3 py-2.5' role='status'>
			<span className='text-[13px] font-medium text-yellow-foreground'>
				The protocol changed. Promoted replies still use the old wording.
			</span>
			<span className='text-xs text-yellow-foreground'>
				Recompiled {formatWhen(outdated.since)}. Since then, {outdated.replies} replies went out on the old wording.
				{lastAlert !== null && ` Alert sent to the promotion channel at ${lastAlert}.`}
			</span>
			<Button size='sm' className='w-fit' disabled={!canEdit || busy} onClick={onPromoteAgain}>
				Review and promote again
			</Button>
		</div>
	);
};

interface OverviewProps {
	view: LivePoolView;
	rows: OverviewRow[];
	/** The active promotion is on. */
	on: boolean;
	autoWrites: number;
	canEdit: boolean;
	busy: boolean;
	sorting: boolean;
	readyRule: string;
	renderCard: (row: OverviewRow) => React.ReactNode;
	onProtocolSwitch: (on: boolean) => void;
	onSort: () => void;
	onPromoteAgain: () => void;
	onHistory: () => void;
}

export const Overview = ({
	view,
	rows,
	on,
	autoWrites,
	canEdit,
	busy,
	sorting,
	readyRule,
	renderCard,
	onProtocolSwitch,
	onSort,
	onPromoteAgain,
	onHistory
}: OverviewProps) => {
	const promoted = rows.filter((row) => isPromoted(row.state));
	const notPromoted = rows.filter((row) => !isPromoted(row.state));
	const writes = writeSummariesOf(rows);
	return (
		<div className='flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-4'>
			{view.outdated && (
				<OutdatedBanner outdated={view.outdated} canEdit={canEdit} busy={busy} onPromoteAgain={onPromoteAgain} />
			)}
			<div className='flex flex-col gap-1'>
				<p className='flex justify-between text-[13px] font-medium text-text-heading'>
					Scenarios <span className='text-[11px] font-normal text-gray-800'>{rows.length} total</span>
				</p>
				<p className='text-[11px] leading-4 text-gray-800'>
					<b className='font-medium text-text-heading'>Auto-send</b> = the reply goes out by itself.{' '}
					<b className='font-medium text-text-heading'>Draft</b> = Kim drafts it and a person sends it.{' '}
					<b className='font-medium text-text-heading'>Ready</b> = {readyRule}.
				</p>
			</div>
			<div className='flex flex-col gap-1 rounded-lg bg-blue-bg px-3 py-2 text-[11px] leading-4 text-blue-foreground'>
				<span>
					{rows.length} scenarios from {view.pool.recorded} live tickets Kim answered on this protocol in the last{' '}
					{view.windowDays} days, and {view.pool.gaps} that fit no scenario. Nothing changes for customers until you
					switch one on.
				</span>
				{view.pool.open > 0 && (
					<span>
						{view.pool.open} new live tickets not sorted yet.{' '}
						<button
							type='button'
							className='cursor-pointer font-medium underline disabled:cursor-default disabled:no-underline'
							onClick={onSort}
							disabled={!canEdit || sorting}
						>
							{sorting ? 'Sorting…' : 'Sort them'}
						</button>
					</span>
				)}
			</div>
			<div className='flex items-center justify-between gap-3 rounded-[10px] border border-gray-500 px-3 py-2.5'>
				<div className='flex flex-col gap-0.5'>
					<span className='text-[13px] font-medium text-text-heading'>Promoted on this protocol</span>
					<span className='text-[11px] text-gray-800'>
						{promotedSummaryOf(
							rows.map((row) => row.state),
							autoWrites
						)}
					</span>
				</div>
				<Switch
					checked={on}
					disabled={!canEdit || busy}
					onCheckedChange={onProtocolSwitch}
					aria-label='Promoted on this protocol'
				/>
			</div>
			<p className='flex justify-between text-[11px] font-medium tracking-wide text-gray-800 uppercase'>
				Promoted <span>{promoted.length}</span>
			</p>
			<div className='flex flex-col gap-2'>
				{promoted.length ? promoted.map(renderCard) : <p className='text-xs text-gray-800'>None yet.</p>}
			</div>
			<p className='flex justify-between text-[11px] font-medium tracking-wide text-gray-800 uppercase'>
				Not promoted <span>{notPromoted.length}</span>
			</p>
			<div className='flex flex-col gap-2'>{notPromoted.map(renderCard)}</div>
			{writes.length > 0 && (
				<div className='flex flex-col gap-2 border-t border-gray-300 pt-3'>
					<p className='text-[13px] font-medium text-text-heading'>Write actions</p>
					<p className='text-[11px] leading-4 text-gray-800'>
						Each scenario has its own auto-approve switch. A No in one scenario never counts against another.
					</p>
					{writes.map((write) => (
						<div
							key={write.tool}
							className='flex items-center justify-between gap-2.5 rounded-[10px] border border-gray-500 px-3 py-2.5'
						>
							<div className='flex flex-col gap-0.5'>
								<span className='font-mono text-xs text-text-heading'>{write.tool}</span>
								<span className='text-[11px] text-gray-800'>
									Used by {write.scenarios} {write.scenarios === 1 ? 'scenario' : 'scenarios'} · auto in {write.auto} ·{' '}
									{write.rejected} rejects in 30 days
								</span>
							</div>
							{write.worst && <Badge variant='destructive'>Reject rate {write.worst.rate} in {write.worst.scenario}</Badge>}
						</div>
					))}
				</div>
			)}
			<button type='button' onClick={onHistory} className='w-fit cursor-pointer text-xs font-medium text-blue-foreground underline'>
				History
			</button>
		</div>
	);
};
